// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Condition, Encounter, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

export function usePatientConditions(patient: Patient | undefined): {
  conditions: Condition[];
  loading: boolean;
  setConditions: (conditions: Condition[]) => void;
  refresh: () => void;
} {
  const medplum = useMedplum();
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [conditionResults, encounters] = await Promise.all([
        medplum.searchResources('Condition', {
          subject: `Patient/${patient.id}`,
          _sort: '-recorded-date',
          _count: '100',
        }),
        medplum.searchResources('Encounter', { subject: `Patient/${patient.id}`, _count: '100' }),
      ]);
      const diagnosisIds = new Set(
        encounters.flatMap((encounter: Encounter) =>
          (encounter.diagnosis ?? []).map((diagnosis) => diagnosis.condition?.reference?.split('/')[1]).filter(Boolean)
        )
      );
      setConditions(
        (conditionResults as Condition[]).filter((condition) => condition.id && !diagnosisIds.has(condition.id))
      );
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    refresh().catch(console.error);
  }, [refresh]);

  return { conditions, loading, setConditions, refresh: () => refresh().catch(console.error) };
}
